import { useContext, useEffect } from 'react';

import LinkList, { LinkAnchorTargets } from '@helsenorge/designsystem-react/components/LinkList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';

import { mapCultureNameToLanguageLocale } from '@helsenorge/core-cms/shared/language';
import { getAbsoluteUrl } from '@helsenorge/core-cms/shared/links';
import TemaMenu from '@helsenorge/core-cms/temamenu-v2';
import { LinkDTO } from '@helsenorge/core-cms/types/entities/cms.v1entities';
import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { Breakpoint, useBreakpoint } from '@helsenorge/designsystem-react';
import { trackNavigation } from '@helsenorge/framework-utils/adobe-analytics';
import { setUserLanguage } from '@helsenorge/framework-utils/hn-language';

import { globalReducer, globalStateContext } from '../../store';
import { getTiles } from '../../store/actions';
import MenuSearch from '../menu-search';

import styles from './styles.module.scss';

const MenuBasic = (): React.JSX.Element => {
  const globalState = useContext(globalStateContext);
  const globalDispatch = useContext(globalReducer);
  const breakpoint = useBreakpoint();

  const handleLinkClick = (link: LinkDTO): void => {
    if (link.text) {
      trackNavigation('toppmeny', link.text, 'sidetopp');
    }
    const locale = mapCultureNameToLanguageLocale(link.cultureName);
    if (locale) {
      setUserLanguage(locale);
    }
  };

  useEffect(() => {
    const language = globalState.language ?? LanguageLocales.NORWEGIAN;

    const shouldFetchAuthenticated = globalState.isAuthorized && !globalState.tiles?.length;
    const shouldFetchPublic = !globalState.isAuthorized && !globalState.tiles?.length;

    if (shouldFetchAuthenticated) {
      getTiles(globalDispatch, 'AuthenticatedTiles/v1', 'Det oppstod en feil ved tjenestefliser', language);
    } else if (shouldFetchPublic) {
      getTiles(globalDispatch, 'PublicTiles/v1', 'Det oppstod en feil ved tjenestefliser', language);
    }
  }, [globalState.isAuthorized, globalState.language]);

  const showSearch = breakpoint < Breakpoint.md;

  // @todo Dette er en midlertidig løsning for å fjerne tjenester fra menyen, som kan
  // fjernes når header.globalMenu.menuItems ikke lenger inneholder tjenester.
  const removeTjenesterFromMenuItems =
    globalState.headerFooter?.header.globalMenu.menuItems[0] &&
    globalState.headerFooter.header.globalMenu.menuItems[0].menuTitle === globalState.headerFooter.header.globalMenu.tjenesterMenuItemTitle;

  return (
    <div className={styles['menu-basic']}>
      {globalState?.error?.headerFooterError && (
        <NotificationPanel variant={'error'} fluid>
          {globalState?.error?.headerFooterError}
        </NotificationPanel>
      )}
      {showSearch && <MenuSearch />}
      {globalState.loading?.isHeaderFooterLoading || globalState.loading?.isTilesLoading ? (
        <Loader />
      ) : (
        globalState.headerFooter?.header.globalMenu.menuItems && (
          <div className={styles['menu-basic__temamenu']}>
            <TemaMenu
              tjenesterMenuItemTitle={globalState.headerFooter.header.globalMenu.tjenesterMenuItemTitle}
              tiles={globalState.tiles}
              menuItems={
                removeTjenesterFromMenuItems
                  ? globalState.headerFooter.header.globalMenu.menuItems.slice(1)
                  : globalState.headerFooter.header.globalMenu.menuItems
              }
            />
          </div>
        )
      )}
      <div>
        <LinkList color="cherry" chevron>
          {globalState.headerFooter?.header.globalMenu.linkList?.map((link, index) => (
            <LinkList.Link
              key={index}
              href={getAbsoluteUrl(link)}
              target={(link.target as LinkAnchorTargets) ?? undefined}
              title={link.title ?? undefined}
              onClick={(): void => handleLinkClick(link)}
              color="cherry"
            >
              {link.text}
            </LinkList.Link>
          ))}
        </LinkList>
      </div>
    </div>
  );
};

export default MenuBasic;
